import React from 'react';
import { Newspaper, Trophy, Film, User, Home, Menu, PenSquare, Gamepad2, Users, Brain } from 'lucide-react';
import { Tab } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { XlevenLogo } from '../Common/XlevenLogo';

interface NavBarProps {
  activeTab: Tab;
  onTabChange: (t: Tab) => void;
  onPostClick?: () => void;
  orientation?: 'horizontal' | 'vertical';
  isModalOpen?: boolean;
}

export const NavBar: React.FC<NavBarProps> = ({ activeTab, onTabChange, onPostClick, orientation = 'horizontal', isModalOpen }) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  
  const navItems = [
    { id: Tab.HOME, icon: Home, label: t(lang, 'home') },
    { id: Tab.NEWS, icon: Newspaper, label: t(lang, 'news') },
    { id: Tab.GAMES, icon: Trophy, label: t(lang, 'games') },
    { id: Tab.COMMUNITY, icon: Users, label: t(lang, 'community') },
    { id: Tab.SHORTS, icon: Film, label: t(lang, 'shorts') },
    { id: Tab.GAME_STORE, icon: Gamepad2, label: t(lang, 'gamestore') },
    { id: Tab.SETTINGS, icon: User, label: t(lang, 'mypage') },
  ];

  const renderIcon = (item: typeof navItems[0], isActive: boolean, size = 24) => {
    if (item.id === Tab.SETTINGS && user) {
        return (
            <div className={`w-7 h-7 rounded-full overflow-hidden border-2 transition-all ${isActive ? 'border-slate-900 scale-110 shadow-sm' : 'border-transparent opacity-70'}`}>
                <img src={user.avatar} className="w-full h-full object-cover" alt="" />
            </div>
        );
    }
    return (
        <item.icon 
            size={size} 
            strokeWidth={isActive ? 2.5 : 2} 
            className={`transition-all duration-300 ${isActive ? 'text-slate-900' : 'text-slate-400'}`}
        />
    );
  };

  if (orientation === 'vertical') {
      return (
          <aside className="h-screen flex flex-col items-center lg:items-start p-4 lg:p-8">
              <div className="mb-8 lg:px-2">
                  <div className="hidden lg:block" onClick={() => onTabChange(Tab.HOME)}>
                    <XlevenLogo />
                  </div>
                  <div className="lg:hidden text-rose-500">
                    <div className="w-10 h-10 bg-rose-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-rose-100">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 17H4M4 17L8 13M4 17L8 21" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M4 7H20M20 7L16 3M20 7L16 11" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                  </div>
              </div>

              <nav className="flex-1 flex flex-col gap-2 w-full">
                  {navItems.filter(item => item.id !== Tab.SETTINGS).map((item) => {
                      const isActive = activeTab === item.id;
                      return (
                          <button
                              key={item.id}
                              onClick={() => onTabChange(item.id)}
                              className={`flex items-center gap-4 p-3 lg:px-4 lg:py-3.5 rounded-full transition-all group relative w-fit lg:w-full ${
                                  isActive 
                                  ? 'bg-slate-50 text-slate-900' 
                                  : 'text-slate-500 md:hover:bg-gray-50'
                              }`}
                          >
                              <div className="shrink-0">
                                  {renderIcon(item, isActive)}
                              </div>
                              <span className={`hidden lg:block text-lg transition-all ${isActive ? 'font-black' : 'font-medium'}`}>
                                  {item.label}
                              </span>
                          </button>
                      );
                  })}

                  {/* Desktop Only "Post" Action Button for News Feed */}
                  {(activeTab === Tab.NEWS || (activeTab === Tab.COMMUNITY && window.getCurrentPath().includes('/stadium'))) && (
                      <div className="mt-4 w-full px-0 lg:px-0">
                          <button
                              onClick={() => {
                                  if (activeTab === Tab.COMMUNITY && onPostClick) {
                                      onPostClick();
                                  } else if (activeTab === Tab.COMMUNITY) {
                                      const path = window.getCurrentPath();
                                      const teamId = path.split('/').pop();
                                      window.navigateTo(`/feed/post?from=stadium&teamId=${teamId}`);
                                  } else {
                                      onTabChange(Tab.NEWS_POST);
                                  }
                              }}
                              className="bg-rose-500 text-white rounded-full lg:rounded-[20px] p-3 lg:px-4 flex items-center justify-start gap-3 md:hover:bg-rose-600 transition-all active:scale-95 group w-fit lg:w-full"
                              title={t(lang, 'post')}
                          >
                              <PenSquare size={24} strokeWidth={2.5} className="shrink-0" />
                              <span className="hidden lg:block font-black text-md uppercase tracking-widest">
                                  {t(lang, 'post')}
                              </span>
                          </button>
                      </div>
                  )}
              </nav>

              <div className="mt-auto w-full lg:px-2 pb-4">
                  {user ? (
                    <button 
                        onClick={() => onTabChange(Tab.SETTINGS)}
                        className={`flex items-center gap-3 p-2 lg:p-3 rounded-full transition-all w-fit lg:w-full md:hover:bg-gray-100 ${activeTab === Tab.SETTINGS ? 'bg-slate-50' : ''}`}
                    >
                        <div className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${activeTab === Tab.SETTINGS ? 'border-rose-500' : 'border-gray-100'}`}>
                            <img src={user.avatar} className="w-full h-full object-cover" alt="" />
                        </div>
                        <div className="hidden lg:flex flex-col text-left min-w-0">
                            <span className={`text-sm truncate ${activeTab === Tab.SETTINGS ? 'font-black text-rose-500' : 'font-black text-slate-900'}`}>{user.name}</span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{user.email.split('@')[0]}</span>
                        </div>
                    </button>
                  ) : (
                    <button 
                        onClick={() => onTabChange(Tab.SETTINGS)}
                        className={`flex items-center gap-4 p-3 lg:px-4 lg:py-3.5 rounded-full transition-all group relative w-fit lg:w-full md:hover:bg-gray-100 ${activeTab === Tab.SETTINGS ? 'bg-slate-50' : ''}`}
                    >
                        <div className="shrink-0 text-slate-400 md:group-hover:text-slate-900">
                            <User size={24} strokeWidth={2.5} className={activeTab === Tab.SETTINGS ? 'text-slate-900' : ''} />
                        </div>
                        <span className={`hidden lg:block text-lg transition-all ${activeTab === Tab.SETTINGS ? 'font-black text-slate-900' : 'font-medium text-slate-500'}`}>
                            {t(lang, 'mypage')}
                        </span>
                    </button>
                  )}
              </div>
          </aside>
      );
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 pb-safe shadow-[0_-8px_30px_rgba(0,0,0,0.04)] transition-all duration-300 ${isModalOpen ? 'z-[-1]' : 'z-[100] opacity-100 visible'}`}>
        <div className="flex justify-around items-center h-16 max-w-lg mx-auto">
            {navItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                    <button
                        key={item.id}
                        onClick={() => onTabChange(item.id)}
                        className="relative flex-1 h-full flex flex-col items-center justify-center transition-transform active:scale-90"
                    >
                        <div className="p-2 transition-all duration-300">
                            {renderIcon(item, isActive, 24)}
                        </div>
                        {isActive && (
                            <div className="absolute top-0 w-8 h-1 bg-slate-900 rounded-b-full" />
                        )}
                    </button>
                );
            })}
        </div>
    </div>
  );
};
